import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, History, RefreshCw, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { webhookDeliveriesQueryOptions } from '@/lib/webhooks';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  webhookId: string | null;
  webhookUrl?: string;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function WebhookDeliveriesDialog({ open, onOpenChange, webhookId, webhookUrl }: Props) {
  const qc = useQueryClient();
  // Only fetch when the dialog is open + we have an id (avoid useless requests
  // when the dialog is closed). enabled gate handles both cases cleanly.
  const query = useQuery({
    ...webhookDeliveriesQueryOptions(webhookId ?? 'noop'),
    enabled: open && webhookId !== null,
  });

  const items = query.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="text-brand-600 h-5 w-5" />
            ประวัติการส่ง
          </DialogTitle>
          <DialogDescription className="break-all font-mono text-xs">
            {webhookUrl ?? ''}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>{items.length > 0 ? `${items.length} รายการล่าสุด (สูงสุด 50)` : ''}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (webhookId) {
                void qc.invalidateQueries({
                  queryKey: webhookDeliveriesQueryOptions(webhookId).queryKey,
                });
              }
            }}
            disabled={query.isFetching}
          >
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${query.isFetching ? 'animate-spin' : ''}`} />
            รีเฟรช
          </Button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto rounded-md border border-slate-200 dark:border-slate-800">
          {query.isLoading ? (
            <div className="p-8 text-center text-sm text-slate-500">กำลังโหลด...</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              ยังไม่มี delivery — รอ event ที่จับคู่ event subscription หรือลอง test fire
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 text-left text-[11px] uppercase text-slate-500 dark:bg-slate-800/50">
                <tr>
                  <th className="px-3 py-2 font-medium">เวลา</th>
                  <th className="px-3 py-2 font-medium">Event</th>
                  <th className="px-3 py-2 font-medium">ผล</th>
                  <th className="px-3 py-2 font-medium">ms</th>
                  <th className="px-3 py-2 font-medium">รายละเอียด</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {items.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                    <td className="whitespace-nowrap px-3 py-2 text-slate-600 dark:text-slate-400">
                      {formatTime(d.deliveredAt)}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-700 dark:text-slate-300">
                      {d.event}
                    </td>
                    <td className="px-3 py-2">
                      {d.ok ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                          <CheckCircle2 className="h-3 w-3" />
                          {d.status}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-400">
                          <XCircle className="h-3 w-3" />
                          {d.status || '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-400">{d.durationMs}</td>
                    <td className="break-all px-3 py-2 text-[11px] text-slate-500">
                      {d.error ?? <span className="text-slate-400">ส่งสำเร็จ</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>ปิด</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
